/* eslint-disable no-param-reassign */
/**
 * "Escolhido pelos campeões": the lead card rotates coldzera -> Sacy -> brTT (6 s each, like the home
 * pro-player carousel), with dots (arrow keys walk them), a pause control, and a pause while hovered,
 * focused, off screen or in a hidden tab; the two small cards always show the other two players.
 *
 * Port of the design's `champions()`: the three players' texts are read from the markup React rendered
 * (already translated) and moved between the cards, so the rotation never re-renders React. Every card
 * holds the tallest of its three states as its min-height, so a swap never moves the band.
 */
import { createRuntime, one } from './runtime';

export interface ChampionsOptions {
  labelPause: string;
  labelResume: string;
  textPause: string;
  textResume: string;
}

interface CardParts {
  img: HTMLImageElement;
  name: HTMLElement;
  title: HTMLElement | null;
  disc: HTMLElement | null;
  pull: HTMLElement | null;
  rest: HTMLElement | null;
  fix: HTMLElement | null;
}

interface PlayerTexts {
  slug: string;
  name: string;
  title: string;
  disc: string;
  pull: string;
  rest: string;
  fix: string;
}

const DURATION = 6000;
const SLUG = /player-([a-z0-9]+)-/;
const SLUG_ALL = /player-[a-z0-9]+-/g;

const text = (element: Element | null) => (element?.textContent ?? '').replace(/\s+/g, ' ').trim();

/** Text after the leading label or rune; a <br> in it travels as "\n". */
function tailText(element: HTMLElement | null): string {
  if (!element) return '';
  let out = '';
  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) out += node.nodeValue ?? '';
    else if (node.nodeName === 'BR') out += '\n';
  });
  return out
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .join('\n')
    .trim();
}

function setTail(element: HTMLElement | null, value: string): void {
  if (!element) return;
  Array.from(element.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE || node.nodeName === 'BR') element.removeChild(node);
  });
  value.split('\n').forEach((line, index) => {
    if (index) element.appendChild(document.createElement('br'));
    element.appendChild(document.createTextNode(line));
  });
}

function slugOf(img: HTMLImageElement): string {
  return SLUG.exec(img.getAttribute('src') ?? '')?.[1] ?? '';
}

/* each player ships two crops (art for the lead photo, face for the medallions): a swap re-points the
   whole <picture> (AVIF source + WebP img) to the new slug */
function setSlug(img: HTMLImageElement, slug: string): void {
  const replacement = `player-${slug}-`;
  const picture = img.parentElement?.tagName === 'PICTURE' ? img.parentElement : null;
  picture?.querySelectorAll('source').forEach((source) => source.setAttribute('srcset', (source.getAttribute('srcset') ?? '').replace(SLUG_ALL, replacement)));
  const srcset = img.getAttribute('srcset');
  if (srcset) img.setAttribute('srcset', srcset.replace(SLUG_ALL, replacement));
  img.setAttribute('src', (img.getAttribute('src') ?? '').replace(SLUG_ALL, replacement));
}

export function startChampions(wrap: HTMLElement, options: ChampionsOptions): () => void {
  const runtime = createRuntime();
  const { later, nextFrame, cancelFrame, listen } = runtime;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const lead = one<HTMLElement>(wrap, '.champ--lead');
  const smalls = Array.from(wrap.querySelectorAll<HTMLElement>('.champ:not(.champ--lead)'));
  const dots = Array.from(wrap.querySelectorAll<HTMLButtonElement>('[data-champ-dot]'));
  const pauseButton = wrap.querySelector<HTMLButtonElement>('[data-champ-pause]');
  if (smalls.length !== 2 || dots.length !== 3) return () => runtime.dispose();

  const parts = (card: HTMLElement, isLead: boolean): CardParts => ({
    img: one<HTMLImageElement>(card, isLead ? '.champ__art img' : '.champ__face img'),
    name: one<HTMLElement>(card, isLead ? '.champ__id--lead b' : '.champ__id b'),
    title: card.querySelector<HTMLElement>(isLead ? '.champ__id--lead small' : '.champ__id small'),
    disc: card.querySelector<HTMLElement>('.champ__disc'),
    pull: card.querySelector<HTMLElement>('.champ__pull'),
    rest: card.querySelector<HTMLElement>('.champ__rest'),
    fix: card.querySelector<HTMLElement>('.champ__fix'),
  });
  const read = (card: CardParts): PlayerTexts => ({ slug: slugOf(card.img), name: text(card.name), title: text(card.title), disc: tailText(card.disc), pull: text(card.pull), rest: text(card.rest), fix: tailText(card.fix) });
  const write = (card: CardParts, player: PlayerTexts) => {
    if (slugOf(card.img) !== player.slug) setSlug(card.img, player.slug);
    card.name.textContent = player.name;
    if (card.title) card.title.textContent = player.title;
    setTail(card.disc, player.disc);
    if (card.pull) card.pull.textContent = player.pull;
    if (card.rest) card.rest.textContent = player.rest;
    setTail(card.fix, player.fix);
  };
  const leadParts = parts(lead, true);
  const smallParts = smalls.map((card) => parts(card, false));
  const players = [read(leadParts), ...smallParts.map(read)];
  const originals = players.map((player) => ({ ...player }));

  /* warm the other players' lead photo and medallion in the sizes the browser picked for these */
  const warm = [leadParts.img.currentSrc || leadParts.img.getAttribute('src') || '', smallParts[0]?.img.currentSrc || smallParts[0]?.img.getAttribute('src') || ''];
  players.forEach((player) =>
    warm.forEach((url) => {
      if (!url) return;
      const image = new Image();
      image.src = url.replace(SLUG, `player-${player.slug}-`);
    })
  );

  let current = 0;
  let elapsed = 0;
  const fill = (index: number) => {
    const at = (offset: number) => players[(index + offset) % 3] as PlayerTexts;
    write(leadParts, at(0));
    if (smallParts[0]) write(smallParts[0], at(1));
    if (smallParts[1]) write(smallParts[1], at(2));
    dots.forEach((dot, dotIndex) => {
      dot.setAttribute('aria-pressed', dotIndex === index ? 'true' : 'false');
      dot.style.setProperty('--p', dotIndex === index && reduced ? '1' : '0');
    });
  };
  let swapTimer = 0;
  const show = (index: number, byUser: boolean) => {
    if (index === current) return;
    current = index;
    elapsed = 0;
    lead.setAttribute('aria-live', byUser ? 'polite' : 'off');
    if (reduced) {
      fill(index);
      return;
    }
    runtime.clearLater(swapTimer);
    wrap.classList.add('is-swapping');
    swapTimer = later(() => {
      wrap.classList.add('is-arriving'); // the new portrait starts at 1.06...
      fill(index);
      nextFrame(() =>
        nextFrame(() => {
          wrap.classList.remove('is-swapping', 'is-arriving'); // ...and settles to 1 over 1.1 s
        })
      );
    }, 360);
  };

  lead.setAttribute('aria-live', 'off');
  dots.forEach((dot, index) => {
    listen(dot, 'click', () => show(index, true));
    listen(dot, 'keydown', (event) => {
      const STEPS: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1 };
      const step = STEPS[event.key] ?? 0;
      if (!step) return;
      event.preventDefault();
      const next = (index + step + 3) % 3;
      dots[next]?.focus();
      show(next, true);
    });
  });
  fill(0);

  /* the three players' texts differ in length: every card holds the tallest of its three states */
  let reserving = false;
  const reserveHeights = () => {
    if (reserving) return;
    if (wrap.classList.contains('is-swapping')) {
      later(reserveHeights, 500);
      return;
    }
    reserving = true;
    const keep = current;
    let leadHeight = 0;
    let smallHeight = 0;
    [lead, ...smalls].forEach((card) => {
      card.style.minHeight = '';
    });
    for (let index = 0; index < 3; index += 1) {
      fill(index);
      leadHeight = Math.max(leadHeight, lead.getBoundingClientRect().height);
      smallHeight = Math.max(smallHeight, ...smalls.map((card) => card.getBoundingClientRect().height));
    }
    fill(keep);
    lead.style.minHeight = `${Math.ceil(leadHeight)}px`;
    smalls.forEach((card) => {
      card.style.minHeight = `${Math.ceil(smallHeight)}px`;
    });
    reserving = false;
  };
  reserveHeights();
  document.fonts?.ready.then(() => {
    if (runtime.isAlive()) reserveHeights();
  });
  let resizeTimer = 0;
  const onResize = () => {
    runtime.clearLater(resizeTimer);
    resizeTimer = later(reserveHeights, 200);
  };
  window.addEventListener('resize', onResize);
  window.addEventListener('load', reserveHeights);
  runtime.onDispose(() => {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('load', reserveHeights);
  });

  const teardown = () => {
    runtime.dispose();
    wrap.classList.remove('is-swapping', 'is-arriving', 'is-settled');
    [lead, ...smalls].forEach((card) => {
      card.style.minHeight = '';
    });
    // hand the markup back exactly as React rendered it
    write(leadParts, originals[0] as PlayerTexts);
    smallParts.forEach((card, index) => write(card, originals[index + 1] as PlayerTexts));
  };

  if (reduced) {
    // no autoplay: coldzera stays until a dot is pressed, and there is nothing to pause
    if (pauseButton) pauseButton.hidden = true;
    return teardown;
  }

  let paused = false;
  let visible = false;
  let hover = false;
  let focus = false;
  let last = 0;
  let frame = 0;
  const running = () => visible && !hover && !focus && !paused && !document.hidden;
  const tick = (now: number) => {
    frame = 0;
    if (!running()) {
      last = 0;
      return;
    }
    const delta = last ? Math.min(120, now - last) : 0;
    last = now;
    elapsed += delta;
    dots[current]?.style.setProperty('--p', Math.min(1, elapsed / DURATION).toFixed(3));
    if (elapsed >= DURATION) show((current + 1) % 3, false);
    frame = nextFrame(tick);
  };
  const loop = () => {
    if (!frame && running()) {
      last = 0;
      frame = nextFrame(tick);
    }
  };
  const label = pauseButton?.querySelector<HTMLElement>('[data-pauselbl]') ?? null;
  const applyPause = (next: boolean) => {
    paused = next;
    pauseButton?.setAttribute('aria-pressed', next ? 'true' : 'false');
    pauseButton?.setAttribute('aria-label', next ? options.labelResume : options.labelPause);
    if (label) label.textContent = next ? options.textResume : options.textPause;
    lead.setAttribute('aria-live', next ? 'polite' : 'off');
    loop();
  };
  if (pauseButton) listen(pauseButton, 'click', () => applyPause(!paused));
  listen(wrap, 'mouseenter', () => {
    hover = true;
  });
  listen(wrap, 'mouseleave', () => {
    hover = false;
    loop();
  });
  listen(wrap, 'focusin', () => {
    focus = true;
  });
  listen(wrap, 'focusout', (event) => {
    if (event.relatedTarget instanceof Node && wrap.contains(event.relatedTarget)) return;
    focus = false;
    loop();
  });
  document.addEventListener('visibilitychange', loop);
  runtime.onDispose(() => document.removeEventListener('visibilitychange', loop));

  let settled = false;
  if (typeof IntersectionObserver === 'function') {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
          if (visible && !settled) {
            settled = true;
            later(() => wrap.classList.add('is-settled'), 1900);
          }
          loop();
        });
      },
      { threshold: [0, 0.2, 0.5] }
    );
    observer.observe(wrap);
    runtime.onDispose(() => observer.disconnect());
  } else {
    visible = true;
    wrap.classList.add('is-settled');
    loop();
  }
  runtime.onDispose(() => {
    if (frame) cancelFrame(frame);
  });
  return teardown;
}
